import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext } from 'react';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { GIFTFINDER_CONFIG } from '../../../config/links/links';
import {
  GIFT_FINDER_FILTER,
  filterPrice,
  FILTER_CATEGORY,
} from '../../../config/setup/setup';
import {
  GIFT_RECIPIENT,
  GIFT_PERSONALITY,
  CLEAR_ALL,
} from '../../../config/text/text';
import { getGiftUrlWithFilterQuery } from '../../../helpers/filters';
import {
  recipientFilters,
  budgetIconFilters,
  personalityFilters,
} from '../../../helpers/giftfinder';
import { fetchJson } from '../../../helpers/jsonConfig';
import { isMobileViewport, useScreenBreakpoint } from '../../../helpers/screen';
import ThemeContext from '../../../providers/ThemeProvider';
import { BudgetFilters } from '../BudgetFilters';
import { FilterPersonality } from '../FilterPersonality';
import { FilterRecipient } from '../FilterRecipient';
import { GiftBanner } from './GiftBanner';
import { GiftFinderFiltersButtons } from './GiftFinderFiltersButtons';

export const GiftFinderFiltersIcons = ({
  searchParameters = new URLSearchParams(),
  filterReset,
  setFilterReset,
}) => {
  const router = useRouter();
  const theme = useContext(ThemeContext);
  const [key, setKey] = useState(GIFT_FINDER_FILTER.BUDGET);
  const [recipientFilter, setRecipientFilter] = useState({});
  const [personalityFilter, setPersonalityFilter] = useState({});
  const giftsForFromQuery = searchParameters.get('giftFor') ?? '';
  const categories = searchParameters.getAll(FILTER_CATEGORY);
  const searchTagsFromQuery = searchParameters.getAll('searchTag');
  const [giftsFor, setGiftsFor] = useState(giftsForFromQuery);
  const [searchTags, setSearchTags] = useState(searchTagsFromQuery);
  const [checkedState, setCheckedState] = useState(new Array(5).fill(false));
  const [sliderValues, setSliderValues] = useState([0, filterPrice.length]);
  const [isMobile, setIsMobile] = useState(false);
  const [banners, setBanners] = useState(null);
  const breakpoint = useScreenBreakpoint();

  useEffect(() => {
    setIsMobile(isMobileViewport());
  }, [breakpoint]);

  useEffect(() => {
    if (searchTags && searchTags.length > 0) {
      if (recipientFilters) {
        recipientFilters.forEach((filter) => {
          if (searchTags.includes(filter.searchTag)) {
            // highlight on page load
            setRecipientFilter(filter);
          }
        });
      }
      if (personalityFilters) {
        personalityFilters.forEach((filter) => {
          if (searchTags.includes(filter.searchTag)) {
            // highlight on page load
            setPersonalityFilter(filter);
          }
        });
      }
    }
  }, [giftsFor, searchTags]);

  const updateFilter = (config) => {
    const url = getGiftUrlWithFilterQuery({
      categories: config.categories,
      filterPrice,
      giftsFor: config.giftsFor,
      searchTags: config.searchTags,
      sliderValues: config.sliderValues,
      sortBy: config.sortBy,
    });

    router.replace(url, url, { scroll: false });
  };

  const updateBudget = (newValues, isAnySelected) => {
    setSliderValues(newValues);
    if (isAnySelected) setKey(GIFT_FINDER_FILTER.FOR);
    updateFilter({
      categories,
      giftsFor,
      searchTags,
      sliderValues: newValues,
      sortBy: '-1',
    });
  };

  const updatePersonality = (updatePersonalityFilter) => {
    const newTags = [...searchTags];
    let isUnselecting = false;

    if (updatePersonalityFilter.searchTag) {
      if (newTags.includes(updatePersonalityFilter.searchTag)) {
        isUnselecting = true;
      }
      // allow only one recipient
      // delete all recipients
      personalityFilters.forEach((filter) => {
        if (newTags.includes(filter.searchTag)) {
          const deleteIndex = newTags.indexOf(filter.searchTag);
          newTags.splice(deleteIndex, 1);
        }
      });
      // newTags now equal all tags that are not recipients
      if (isUnselecting) {
        // if isUnselecting remove current filter
        setPersonalityFilter({});
      } else {
        // add one recipient if not isUnselecting
        newTags.push(updatePersonalityFilter.searchTag);
        setPersonalityFilter(updatePersonalityFilter);
      }
      setSearchTags(newTags);
    }

    updateFilter({
      categories,
      giftsFor,
      searchTags: newTags,
      sliderValues,
      sortBy: '-1',
    });
  };

  const updateRecipient = (updateRecipientFilter) => {
    const newTags = [...searchTags];
    let newGiftFor = updateRecipientFilter.oldKey;
    let isUnselecting = false;

    if (updateRecipientFilter.searchTag) {
      if (newTags.includes(updateRecipientFilter.searchTag)) {
        isUnselecting = true;
      }
      // allow only one recipient
      // delete all recipients
      recipientFilters.forEach((filter) => {
        if (newTags.includes(filter.searchTag)) {
          const deleteIndex = newTags.indexOf(filter.searchTag);
          newTags.splice(deleteIndex, 1);
        }
      });
      // newTags now equal all tags that are not recipients
      if (isUnselecting) {
        // if isUnselecting remove current filter
        newGiftFor = '';
        setGiftsFor(newGiftFor);
        setRecipientFilter({});
      } else {
        // add one recipient if not isUnselecting
        newTags.push(updateRecipientFilter.searchTag);
        setGiftsFor(newGiftFor);
        setRecipientFilter(updateRecipientFilter);
        setKey(GIFT_FINDER_FILTER.TYPE);
      }
      setSearchTags(newTags);
    }

    updateFilter({
      categories,
      giftsFor: newGiftFor,
      searchTags: newTags,
      sliderValues,
      sortBy: '-1',
    });
  };

  const reset = () => {
    setCheckedState(new Array(5).fill(false));
    setSliderValues([0, 100]);
    setRecipientFilter({});
    updateRecipient(recipientFilter);
    setSearchTags([]);
    setPersonalityFilter({});
    updateFilter({
      categories: [],
      giftsFor: '',
      searchTags: [],
      sliderValues: [0, budgetIconFilters.length - 1],
      sortBy: '-1',
    });
    setKey(GIFT_FINDER_FILTER.BUDGET);
  };

  useEffect(() => {
    if (filterReset) {
      reset();
      setFilterReset(false);
    }
  }, [filterReset]);

  useEffect(() => {
    async function getBanners() {
      const configUrl =
        GIFTFINDER_CONFIG[process.env.NEXT_PUBLIC_ENVIRONMENT || 'prod'][
          process.env.NEXT_PUBLIC_SITE || 'wowcher'
        ];
      const bannersFromConfig = await fetchJson(configUrl);
      if (bannersFromConfig?.banners) {
        setBanners(bannersFromConfig.banners);
      }
    }
    getBanners();
  }, []);

  return (
    <>
      <GiftBanner banners={banners} />
      <div className="button-wrapper">
        <button className="clearAll-button" onClick={reset} type="reset">
          {CLEAR_ALL}
        </button>
      </div>
      {isMobile && (
        <div className="mobileTabs">
          <Tabs
            activeKey={key}
            id="filter-tabs"
            onSelect={(selectedKey) => setKey(selectedKey)}
          >
            <Tab
              eventKey={GIFT_FINDER_FILTER.BUDGET}
              title={GIFT_FINDER_FILTER.BUDGET}
            >
              <BudgetFilters
                checkedState={checkedState}
                setCheckedState={setCheckedState}
                setSliderValues={setSliderValues}
                sliderValues={sliderValues}
                updateFilter={updateBudget}
              />
            </Tab>
            <Tab
              eventKey={GIFT_FINDER_FILTER.FOR}
              title={GIFT_FINDER_FILTER.FOR}
            >
              <h2 className="filter-heading">{GIFT_RECIPIENT}</h2>
              <FilterRecipient
                currentFilter={recipientFilter}
                filters={recipientFilters}
                updateRecipient={updateRecipient}
              />
            </Tab>
            <Tab
              eventKey={GIFT_FINDER_FILTER.TYPE}
              title={GIFT_FINDER_FILTER.TYPE}
            >
              <h2 className="filter-heading">{GIFT_PERSONALITY}</h2>
              <FilterPersonality
                currentFilter={personalityFilter}
                filters={personalityFilters}
                updatePersonality={updatePersonality}
              />
            </Tab>
          </Tabs>
          <GiftFinderFiltersButtons
            activeKey={key}
            onClick={(button) => setKey(button)}
          />
        </div>
      )}
      {!isMobile && (
        <>
          <div className="budgetIcons">
            <BudgetFilters
              checkedState={checkedState}
              setCheckedState={setCheckedState}
              setSliderValues={setSliderValues}
              sliderValues={sliderValues}
              updateFilter={updateBudget}
            />
          </div>
          <div className="recipient-icons">
            <h2 className="filter-heading">{GIFT_RECIPIENT}</h2>
            <FilterRecipient
              currentFilter={recipientFilter}
              filters={recipientFilters}
              updateRecipient={updateRecipient}
            />
          </div>
          <div className="personality-icons">
            <h2 className="filter-heading">{GIFT_PERSONALITY}</h2>
            <FilterPersonality
              currentFilter={personalityFilter}
              filters={personalityFilters}
              updatePersonality={updatePersonality}
            />
          </div>
        </>
      )}

      <style jsx>
        {`
          .mobileTabs {
            padding-right: 10%;
            padding-left: 10%;
            padding-top: 3%;
          }
          .budgetIcons {
            padding-right: 10%;
            padding-left: 10%;
          }
          .button-wrapper {
            padding-bottom: 20px;
            padding-left: 150px;
          }
          .clearAll-button {
            float: right;
            border-style: none;
            border-radius: 18px;
            padding: 7px;
            background-color: ${theme.colors.giftBannerBackground};
            margin-right: 10%;
            margin-top: 10px;
            font-size: 16px;
            text-align: center;
            font-weight: bold;
          }
          .recipient-icons {
            padding-right: 10%;
            padding-left: 10%;
            padding-bottom: 2%;
          }
          .personality-icons {
            padding-right: 10%;
            padding-left: 10%;
            padding-bottom: 3%;
          }
          .filter-heading {
            font-size: 15px;
            font-weight: bold;
            margin-top: 5px;
            padding: 5px 0 10px 0;
          }
          @media (max-width: ${theme.breakpoints.smDown}) {
            .clearAll-button {
              float: right;
              border-style: none;
              border-radius: 14px;
              padding: 5px;
              background-color: ${theme.colors.giftBannerBackground};
              font-size: 11px;
              text-align: center;
              font-weight: bold;
            }
            .button-wrapper {
              padding-bottom: 5px;
              padding-left: 150px;
            }
          }
        `}
      </style>
    </>
  );
};

GiftFinderFiltersIcons.propTypes = {
  filterReset: PropTypes.bool,
  searchParameters: PropTypes.object,
  setFilterReset: PropTypes.func,
};
